import React from 'react'
import { useNavigate } from 'react-router-dom'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import _ from 'underscore'
import styled from 'styled-components'
import EditIcon from '@mui/icons-material/Edit'
import { DragDropProvider, DragEndEvent, PointerSensor } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { color } from 'styles/colors'

import StatusCell from 'pages/shared/StatusCell'

import Table from 'components/Table'

import { useStoreContext } from 'hooks/StoreContext'
import useDeviceMetrics from 'hooks/useDeviceMetrics'
import useGroups from 'hooks/useGroups'

import { Circle, LoadingSinglePanePage } from 'styles/shared'
import { SortedGroup, SortedMetric, Trend } from 'types/DeviceMetric'

type SortableTrendRowsProps = {
  items: Trend[],
  onSortEndCallback: (payload: { oldIndex: number, newIndex: number }) => void,
  isEditing: boolean
}

type SortableTrend = Trend & {
  id: NonNullable<Trend['id']>
}

type SortableTrendRowProps = {
  trend: SortableTrend
  index: number
}

const SortableTrendRows = ({ items, isEditing, onSortEndCallback }: SortableTrendRowsProps) => {
  useDeviceMetrics()

  const { groups, areGroupsLoaded } = useGroups()

  const { deviceMetrics, areDeviceMetricsLoaded } = useStoreContext()

  const navigate = useNavigate()
  const trendList = [...items].sort((a, b) => a.sortPosition - b.sortPosition)

  if (!areGroupsLoaded || !areDeviceMetricsLoaded) {
    return <LoadingSinglePanePage />
  }

  const combinedList = (metricList: SortedMetric[] | null, groupList: SortedGroup[] | null) => {
    return [
      ...(metricList || []).map((list: SortedMetric) => {
        const metric = _.find(deviceMetrics, (metric) => metric.id === list.metricId)

        return {
          id: list.metricId,
          name: metric?.name,
          sortPosition: list.sortPosition,
          color: metric?.color,
          type: 'metric'
        }
      }),
      ...(groupList || []).map((list: SortedGroup) => ({
        id: list.groupId,
        name: groups.find((group) => group.groupId === list.groupId)?.name,
        sortPosition: list.sortPosition,
        color: undefined,
        type: 'group'
      }))
    ]
  }

  const onSortEnd = (event: DragEndEvent) => {
    if (event.canceled) return

    const { source, target } = event.operation
    if (!source || !target || source.id === target.id) return

    const oldIndex = trendList.findIndex((trend) => trend.id === source.id)
    const newIndex = trendList.findIndex((trend) => trend.id === target.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      onSortEndCallback({ oldIndex, newIndex })
    }
  }

  const SortableTrendRow = ({ trend, index }: SortableTrendRowProps) => {
    const sortable = useSortable({
      id: trend.id,
      index,
      disabled: !isEditing
    })

    return (
      <S.Row ref={sortable.ref}>
        <S.Cell>
          {isEditing && (
            <S.DragHandleButton type="button" ref={sortable.handleRef} aria-label={`Reorder trend ${trend.name}`}>
              <S.DragHandle />
            </S.DragHandleButton>
          )}
          <S.Name>{trend.name}</S.Name>
        </S.Cell>
        <S.Cell>
          <S.ListContainer>
            {_.sortBy(combinedList(trend.metricList, trend.groupList), ['sortPosition']).map((item) => (
              <li key={`${item.type}-${item.id}`}>
                <Circle color={item.color} />
                <span>{item.name}</span>
              </li>
            ))}
          </S.ListContainer>
        </S.Cell>
        <div>
          <StatusCell isEnabled={trend.draft} />
          <EditIcon
            fontSize="small"
            color="primary"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/admin/trends/${trend.id}/edit`)}
            aria-label={`Edit Trend metric with title: ${trend.name}`}
          >
            Edit
          </EditIcon>
        </div>
      </S.Row>
    )
  }

  const validTrendList: SortableTrend[] = trendList.filter((trendItem): trendItem is SortableTrend => trendItem.id !== undefined)

  return (
    <DragDropProvider sensors={[PointerSensor]} onDragEnd={onSortEnd}>
      <div>
        {validTrendList.map((trendItem, index) => (
          <SortableTrendRow key={trendItem.id} trend={trendItem} index={index} />
        ))}
      </div>
    </DragDropProvider>
  )
}

export default SortableTrendRows

const S = {
  Cell: styled(Table.Cell)`
    padding-left: 20px;
    align-items: center;
  `,
  Name: styled.p`
    overflow-wrap: break-word;
  `,
  Row: styled(Table.Row)`
    display: grid;
    grid-template-columns: 30% 40% 30%;
    align-items: center;
    min-height: 50px;
    padding-top: 10px;
    padding-bottom: 10px;
  `,
  ListContainer: styled.div`
    display: flex;
    flex-direction: column;
    list-style-type: none;
  `,
  DragHandle: styled(DragHandleIcon)`
    cursor: grab;
    margin-right: 10px;
  `,
  DragHandleButton: styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;
    cursor: grab;
  `,
  ButtonWithIcon: styled.span`
    display: flex;
    align-items: center;
    color: ${color('paneBackground')};
    gap: 5px;
    font-weight: 500;
  `
}
